import styles from './AgentVerificationDocumentsStatus.module.scss'

import {
  AVDocumentsPartner,
  AVDocumentsStatus,
  type AgentVerificationDocumentsMessages,
  type AgentVerificationDocumentsStatusProps
} from './AgentVerificationDocumentsStatus.types'
import { useTranslation } from 'react-i18next'
import { AgentVerificationStatus } from '@percent/cause-dashboard/services/agentVerification/AgentVerification.types'
import { AnimatedContainer, Button, SuccessIcon, ErrorIcon } from '@percent/lemonade'
import { useNavigate } from 'react-router-dom'
import { getSignUpUrl } from '@percent/cause-dashboard/common/utility/utility'
import { SignUpReason } from '@percent/cause-dashboard/common/hooks'
import { HELP_LINK } from '@percent/cause-dashboard/constants/support'
import { PartnerBanner } from '../partnerBanner/PartnerBanner'

const documentsMessages: AgentVerificationDocumentsMessages = {
  expired: {
    title: 'agentVerificationDocuments.status.expired.title',
    description: 'agentVerificationDocuments.status.expired.description'
  },
  processed: {
    title: 'agentVerificationDocuments.status.processed.title',
    description: 'agentVerificationDocuments.status.processed.description'
  },
  submitted: {
    title: 'agentVerificationDocuments.status.submitted.title',
    description: 'agentVerificationDocuments.status.submitted.description',
    claimDescription: 'agentVerificationDocuments.status.submitted.claimDescription'
  },
  pending: {
    title: 'agentVerificationDocuments.status.pending.title',
    description: 'agentVerificationDocuments.status.pending.description'
  },
  cap_exhausted: {
    title: 'agentVerificationDocuments.reupload.capExhausted.title',
    description: 'agentVerificationDocuments.reupload.capExhausted.description'
  }
}

export const AgentVerificationDocumentsStatus = ({
  status,
  agentVerificationStatus,
  partnerName,
  email,
  organisationId,
  partnerBanner
}: Readonly<AgentVerificationDocumentsStatusProps>) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { title, description, claimDescription } = documentsMessages[status]
  const isExpired = status === AVDocumentsStatus.EXPIRED
  const isError = isExpired || status === AVDocumentsStatus.CAP_EXHAUSTED
  const isRejected = agentVerificationStatus === AgentVerificationStatus.REJECTED
  const isGooglePartner = partnerName?.toLowerCase() === AVDocumentsPartner.GOOGLE
  const isClaimSubmission = status === AVDocumentsStatus.SUBMITTED && partnerName === AVDocumentsPartner.CLAIM

  const handleSignUpClick = () => {
    const url = getSignUpUrl({
      organizationId: organisationId,
      email,
      signUpReason: SignUpReason.AV_DOCUMENT_UPLOAD
      // TODO: get partnerId
    })
    navigate(url)
  }

  return (
    <div className={styles.documentsStatusContainer}>
      <PartnerBanner banner={partnerBanner} />
      <div className={styles.iconContainer}>
        {!isRejected && (
          <AnimatedContainer>
            {isError ? (
              <div data-testid="error-icon">{ErrorIcon}</div>
            ) : (
              <div data-testid="success-icon">{SuccessIcon}</div>
            )}
          </AnimatedContainer>
        )}
      </div>

      <h5>{t(title)}</h5>
      <p>
        {t(isClaimSubmission ? claimDescription! : description, { partnerName })}{' '}
        {isError && (
          <a href={HELP_LINK} target="_blank" rel="noreferrer" className={styles.supportLink}>
            help.goodstack.org
          </a>
        )}
      </p>

      {isGooglePartner && !isError && !isRejected && (
        <div className={styles.googleSetupContainer}>
          <h6>{t('agentVerificationDocuments.googleCTA.title')}</h6>
          <p>{t('agentVerificationDocuments.googleCTA.description')}</p>
          <Button onClick={handleSignUpClick}>{t('typography.experiments.ctaButton')}</Button>
        </div>
      )}
    </div>
  )
}
